import {
  KeyboardEvent, ReactElement, useId, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { SnowflakeIcon } from '~/icons';
import { SchedulePermission } from '../../schedules';
import { BlockIcon } from '../icons';
import {
  ModalLabel, PermissionHint, PermissionOption, PermissionSegment,
} from './styles';

interface PermissionFieldProps {
  value: SchedulePermission;
  onChange: (value: SchedulePermission) => void;
  testId?: string;
}

interface PermissionOptionConfig {
  value: SchedulePermission;
  labelKey: string;
  hintKey: string;
  renderIcon: () => ReactElement;
}

const OPTIONS: PermissionOptionConfig[] = [
  {
    value: 'allow', labelKey: 'ecoSimplificadoPermitirRefrigeracao', hintKey: 'ecoSimplificadoPermitirRefrigeracaoDica', renderIcon: () => <SnowflakeIcon size="16" />,
  },
  {
    value: 'forbid', labelKey: 'ecoSimplificadoBloquear', hintKey: 'ecoSimplificadoBloquearDica', renderIcon: () => <BlockIcon size={16} />,
  },
];

const ARROW_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

/**
 * Funcionamento ("Permitir refrigeração" / "Bloquear") dos modais de programação e de exceção, como controle
 * segmentado: as duas opções ficam visíveis e só a escolhida fica preenchida, sem a ambiguidade do switch.
 */
export function PermissionField({ value, onChange, testId }: Readonly<PermissionFieldProps>): ReactElement {
  const { t } = useTranslation();
  const labelId = useId();
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selected = OPTIONS.find((option) => option.value === value) ?? OPTIONS[0];

  // Setas alternam a opção, como num grupo de rádio nativo.
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (!ARROW_KEYS.includes(event.key)) return;
    event.preventDefault();
    const next = OPTIONS.findIndex((option) => option.value !== value);
    onChange(OPTIONS[next].value);
    optionRefs.current[next]?.focus();
  }

  return (
    <>
      <ModalLabel as="div" id={labelId} style={{ marginTop: 22, marginBottom: 10 }}>{t('funcionamento')}</ModalLabel>
      <PermissionSegment role="radiogroup" aria-labelledby={labelId} onKeyDown={onKeyDown} data-testid={testId}>
        {OPTIONS.map(({ value: optionValue, labelKey, renderIcon }, index) => {
          const active = optionValue === value;
          return (
            <PermissionOption
              key={optionValue}
              ref={(element) => { optionRefs.current[index] = element; }}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              $active={active}
              onClick={() => onChange(optionValue)}
              data-testid={testId ? `${testId}-${optionValue}` : undefined}
            >
              {renderIcon()}
              {t(labelKey)}
            </PermissionOption>
          );
        })}
      </PermissionSegment>
      <PermissionHint>{t(selected.hintKey)}</PermissionHint>
    </>
  );
}
